import { useEffect, useState } from "react";
import api from "../lib/api";
import { fmtTRY } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Tractor, Wrench, PackageSearch, Users, TrendingUp, TrendingDown,
  AlertTriangle, FileText, Wallet
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

function KPI({ icon: Icon, label, value, hint, tone = "pine", testid }) {
  const tones = {
    pine: "bg-[#1B4D3E] text-white",
    ochre: "bg-[#D97706] text-white",
    slate: "bg-slate-900 text-white",
    danger: "bg-rose-600 text-white",
  };
  return (
    <Card className="border-slate-200 tp-fade" data-testid={testid}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
            <div className="mt-1.5 text-2xl sm:text-3xl font-extrabold text-slate-900 truncate">{value}</div>
            {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
          </div>
          <div className={`h-10 w-10 rounded-xl grid place-items-center ${tones[tone]}`}>
            <Icon size={20} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/dashboard/summary").then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="text-slate-500">Yükleniyor…</div>;

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B4D3E]">Genel Bakış</div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">Pano</h1>
        </div>
        <Badge variant="outline" className="hidden sm:inline-flex border-slate-300 text-slate-600">
          {new Date().toLocaleDateString("tr-TR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
        </Badge>
      </div>

      {!data.finance_hidden && (<>
      {/* Today KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KPI icon={TrendingUp} label="Bugünkü Gelir" value={fmtTRY(data.today.gelir)} tone="pine" testid="kpi-today-gelir" />
        <KPI icon={TrendingDown} label="Bugünkü Gider" value={fmtTRY(data.today.gider)} tone="danger" testid="kpi-today-gider" />
        <KPI icon={Wallet} label="Bugünkü Tahsilat" value={fmtTRY(data.today.tahsilat)} tone="ochre" testid="kpi-today-tahsilat" />
        <KPI icon={TrendingUp} label="Aylık Net" value={fmtTRY(data.month.net)} hint={`Gelir ${fmtTRY(data.month.gelir)}`} tone="slate" testid="kpi-month-net" />
      </div>

      {/* Chart */}
      <Card className="border-slate-200">
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Son 30 Gün</div>
              <div className="text-lg font-bold text-slate-900">Nakit Akışı</div>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.cashflow_30d} margin={{ left: -10, right: 10, top: 5, bottom: 0 }}>
                <defs>
                  <linearGradient id="gelirG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1B4D3E" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#1B4D3E" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="giderG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#EA580C" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#EA580C" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(d) => d?.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => v >= 1000 ? `${Math.round(v/1000)}k` : v} />
                <Tooltip formatter={(v) => fmtTRY(v)} labelFormatter={(l) => l} />
                <Area type="monotone" dataKey="gelir" stroke="#1B4D3E" fill="url(#gelirG)" strokeWidth={2} />
                <Area type="monotone" dataKey="gider" stroke="#EA580C" fill="url(#giderG)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      </>)}

      {/* Counts */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        <KPI icon={Tractor} label="Stok Traktör" value={data.counts.tractors_stock} tone="pine" testid="kpi-tractor-stock" />
        <KPI icon={Tractor} label="Satılan Traktör" value={data.counts.tractors_sold} tone="ochre" testid="kpi-tractor-sold" />
        <KPI icon={PackageSearch} label="Parça Çeşidi" value={data.counts.parts} tone="slate" testid="kpi-parts" />
        <KPI icon={AlertTriangle} label="Kritik Stok" value={data.counts.low_stock} tone="danger" testid="kpi-lowstock" />
        <KPI icon={Wrench} label="Açık İş Emri" value={data.counts.open_work_orders} tone="pine" testid="kpi-open-wo" />
        <KPI icon={FileText} label="Bekleyen Teklif" value={data.counts.pending_quotes} tone="ochre" testid="kpi-pending-quote" />
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-slate-900 text-white grid place-items-center"><Users size={18} /></div>
            <div>
              <div className="text-sm font-semibold text-slate-900">Kayıtlı müşteri sayısı</div>
              <div className="text-xs text-slate-500">Müşteri kartlarını, teklif ve iş emrine bağlayabilirsin.</div>
            </div>
          </div>
          <div className="text-3xl font-extrabold text-[#1B4D3E]" data-testid="kpi-customers">{data.counts.customers}</div>
        </CardContent>
      </Card>
    </div>
  );
}
